import type { FC, PointerEvent as ReactPointerEvent, ReactNode } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { ArrowRight, ChevronDown, Menu } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { LiveDot } from '@/components/generals/live-dot'
import { ButtonLink } from '@/components/generals/button'
import { MobileMenu } from '@/components/generals/mobile-menu'
import { mainNav, platformProducts } from '@/constants/navigation'

/**
 * Floating pill header. Transparent at the top, soft blurred glass once the
 * page scrolls, and dark glass whenever it sits over a section marked
 * data-header-theme="dark".
 */
export const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const barRef = useRef<HTMLDivElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const closeTimer = useRef<number | null>(null)
    const [scrolled, setScrolled] = useState(false)
    const [dark, setDark] = useState(false)
    const [megaOpen, setMegaOpen] = useState(false)
    const [mobileOpen, setMobileOpen] = useState(false)

    useEffect(() => {
        let frame = 0

        const update = () => {
            frame = 0
            setScrolled(window.scrollY > 10)

            const bar = barRef.current
            const probe = bar ? bar.getBoundingClientRect().top + bar.offsetHeight / 2 : 40
            const sections = Array.from(document.querySelectorAll<HTMLElement>('[data-header-theme]'))
            let theme = 'light'

            for (const section of sections) {
                const rect = section.getBoundingClientRect()

                if (rect.top <= probe && rect.bottom >= probe) theme = section.dataset.headerTheme ?? 'light'
            }

            setDark(theme === 'dark')
        }

        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', schedule, { passive: true })
        window.addEventListener('resize', schedule)

        return () => {
            cancelAnimationFrame(frame)
            window.removeEventListener('scroll', schedule)
            window.removeEventListener('resize', schedule)
        }
    }, [router.asPath])

    useEffect(() => {
        const close = () => {
            setMegaOpen(false)
            setMobileOpen(false)
        }

        router.events.on('routeChangeStart', close)
        router.events.on('hashChangeStart', close)

        return () => {
            router.events.off('routeChangeStart', close)
            router.events.off('hashChangeStart', close)
        }
    }, [router.events])

    useEffect(() => {
        if (!megaOpen) return

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return

            setMegaOpen(false)
            triggerRef.current?.focus()
        }

        const onPointerDown = (event: PointerEvent) => {
            const target = event.target as Node

            if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return

            setMegaOpen(false)
        }

        document.addEventListener('keydown', onKeyDown)
        document.addEventListener('pointerdown', onPointerDown)

        return () => {
            document.removeEventListener('keydown', onKeyDown)
            document.removeEventListener('pointerdown', onPointerDown)
        }
    }, [megaOpen])

    useEffect(() => {
        return () => {
            if (closeTimer.current) window.clearTimeout(closeTimer.current)
        }
    }, [])

    const openOnHover = (event: ReactPointerEvent) => {
        if (event.pointerType !== 'mouse') return
        if (closeTimer.current) window.clearTimeout(closeTimer.current)

        setMegaOpen(true)
    }

    const closeOnLeave = (event: ReactPointerEvent) => {
        if (event.pointerType !== 'mouse') return
        if (closeTimer.current) window.clearTimeout(closeTimer.current)

        closeTimer.current = window.setTimeout(() => setMegaOpen(false), 160)
    }

    const closeMobile = useCallback(() => setMobileOpen(false), [])

    const isActive = (href: string) => router.pathname === href || router.pathname.startsWith(`${href}/`)
    const platformActive = isActive('/platform')
    const glass = scrolled || megaOpen

    const linkTone = dark
        ? 'text-white/70 hover:text-white focus-visible:ring-white/60'
        : 'text-ink/70 hover:text-ink focus-visible:ring-ultramarine'

    const activeTone = dark ? 'text-white' : 'text-ink'

    return (
        <>
            <header className='pointer-events-none fixed inset-x-0 top-3 z-50 px-3 sm:top-4'>
                <div
                    ref={barRef}
                    className={cn(
                        'pointer-events-auto relative mx-auto flex h-14 w-full max-w-[1120px] items-center justify-between gap-4 rounded-full border pl-4 pr-2 transition-[background-color,border-color,box-shadow,color] duration-500 ease-calm sm:pl-5',
                        dark && 'border-white/10 bg-charcoal/70 text-white shadow-[0_12px_32px_-16px_rgba(0,0,0,0.8)] backdrop-blur-xl',
                        !dark && glass && 'border-ink/10 bg-cream/75 text-ink shadow-[0_12px_32px_-18px_rgba(22,20,15,0.3)] backdrop-blur-xl',
                        !dark && !glass && 'border-transparent bg-transparent text-ink'
                    )}
                >
                    <Link
                        href='/'
                        aria-label='Quayside home'
                        className='rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine'
                    >
                        <Logo />
                    </Link>

                    <nav aria-label='Main' className='hidden lg:block'>
                        <ul className='flex items-center gap-1'>
                            <li onPointerEnter={openOnHover} onPointerLeave={closeOnLeave}>
                                <button
                                    ref={triggerRef}
                                    type='button'
                                    aria-expanded={megaOpen}
                                    aria-controls='platform-menu'
                                    onClick={() => setMegaOpen(value => !value)}
                                    className={cn(
                                        'inline-flex h-10 items-center gap-1 rounded-full px-3.5 text-[14px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2',
                                        linkTone,
                                        (platformActive || megaOpen) && activeTone
                                    )}
                                >
                                    Platform
                                    <ChevronDown aria-hidden='true' className={cn('size-3.5 transition-transform duration-300', megaOpen && 'rotate-180')} />
                                </button>
                            </li>

                            {mainNav.map(link => (
                                <li key={link.href}>
                                    <Link
                                        href={link.href}
                                        aria-current={isActive(link.href) ? 'page' : undefined}
                                        className={cn(
                                            'inline-flex h-10 items-center rounded-full px-3.5 text-[14px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2',
                                            linkTone,
                                            isActive(link.href) && activeTone
                                        )}
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='flex items-center gap-1.5 sm:gap-2'>
                        <Link
                            href='/sign-in'
                            className={cn(
                                'hidden h-10 items-center rounded-full px-3 text-[14px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 lg:inline-flex',
                                linkTone
                            )}
                        >
                            Sign in
                        </Link>

                        <ButtonLink
                            href='/contact'
                            size='sm'
                            variant={dark ? 'light' : 'primary'}
                            className='hidden sm:inline-flex'
                        >
                            Book a walkthrough
                        </ButtonLink>

                        <button
                            type='button'
                            onClick={() => setMobileOpen(true)}
                            aria-label='Open menu'
                            aria-expanded={mobileOpen}
                            aria-controls='mobile-menu'
                            className={cn(
                                'grid size-10 place-items-center rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 lg:hidden',
                                dark ? 'border-white/15 text-white hover:bg-white/10 focus-visible:ring-white/60' : 'border-ink/15 bg-paper/60 text-ink hover:bg-paper focus-visible:ring-ultramarine'
                            )}
                        >
                            <Menu aria-hidden='true' className='size-[18px]' />
                        </button>
                    </div>

                    <div
                        id='platform-menu'
                        ref={menuRef}
                        inert={!megaOpen}
                        onPointerEnter={openOnHover}
                        onPointerLeave={closeOnLeave}
                        className={cn(
                            'absolute left-1/2 top-full hidden w-[min(700px,calc(100vw-24px))] -translate-x-1/2 pt-3 transition-[opacity,translate,visibility] duration-300 ease-calm lg:block',
                            megaOpen ? 'visible translate-y-0 opacity-100' : 'invisible translate-y-1.5 opacity-0'
                        )}
                    >
                        <div className='rounded-[28px] border border-ink/10 bg-paper/95 p-2.5 text-ink shadow-lift backdrop-blur-xl'>
                            <ul className='grid grid-cols-2 gap-1'>
                                {platformProducts.map(product => {
                                    const Icon = product.icon

                                    return (
                                        <li key={product.id}>
                                            <Link
                                                href={product.href}
                                                className='group/item flex h-full gap-4 rounded-[20px] p-4 transition-colors hover:bg-cream focus-visible:bg-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine'
                                            >
                                                <span className='grid size-11 shrink-0 place-items-center rounded-xl border border-ink/10 bg-cream text-ink transition-colors group-hover/item:border-ultramarine/30 group-hover/item:text-ultramarine'>
                                                    <Icon aria-hidden='true' className='size-5' strokeWidth={1.6} />
                                                </span>

                                                <span>
                                                    <span className='block text-[15px] font-semibold tracking-[-0.015em]'>
                                                        {product.name}
                                                    </span>

                                                    <span className='mt-1 block text-[13px] leading-snug text-stone'>
                                                        {product.description}
                                                    </span>
                                                </span>
                                            </Link>
                                        </li>
                                    )
                                })}
                            </ul>

                            <div className='mt-1.5 flex items-center justify-between gap-4 rounded-[20px] bg-cream px-4 py-3 text-[13px]'>
                                <span className='flex items-center gap-2.5 text-ink-soft'>
                                    <LiveDot />
                                    3,400+ live connections, maintained by our team
                                </span>

                                <Link
                                    href='/network'
                                    className='group/link inline-flex items-center gap-1 font-medium text-ultramarine hover:text-ultramarine-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine'
                                >
                                    Browse the network
                                    <ArrowRight aria-hidden='true' className='size-3.5 transition-transform group-hover/link:translate-x-0.5' />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <MobileMenu open={mobileOpen} onClose={closeMobile} />
        </>
    )
}
